import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, ArrowRight, Plus } from "lucide-react";

const DISC = ["music","dance","comedy","film","writing","visual_art","fashion","theatre","design"];

export function ProfileSection() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: "", discipline: "music", city: "Mumbai", bio: "", aesthetic: "", portfolio_urls: [], profile_image_url: "", instagram: "", website: "", rate_card: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => {
      if (data && data.name) setProfile((p) => ({ ...p, ...data }));
      else if (user) setProfile((p) => ({ ...p, name: user.name }));
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try { await api.post("/profile", profile); toast.success("Profile saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setProfile((p) => ({ ...p, profile_image_url: fullUrl }));
      toast.success("Portrait uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <p className="meta-mono mb-6">— PROFILE · DISCOVERY-READY</p>
      <div className="grid sm:grid-cols-2 gap-px bg-border">
        <Inp label="Artist name" v={profile.name} on={(v) => setProfile({ ...profile, name: v })} testid="prof-name" />
        <Sel label="Discipline" v={profile.discipline} on={(v) => setProfile({ ...profile, discipline: v })} opts={DISC} testid="prof-discipline" />
        <Inp label="City" v={profile.city} on={(v) => setProfile({ ...profile, city: v })} testid="prof-city" />
        <Inp label="Instagram" v={profile.instagram} on={(v) => setProfile({ ...profile, instagram: v })} testid="prof-ig" />
        <Inp label="Website" v={profile.website} on={(v) => setProfile({ ...profile, website: v })} testid="prof-web" />
        <Inp label="Rate card / from" v={profile.rate_card} on={(v) => setProfile({ ...profile, rate_card: v })} testid="prof-rate" />
        <div className="bg-background p-5 sm:col-span-2">
          <p className="meta-mono mb-3">— AESTHETIC (3–5 words)</p>
          <input data-testid="prof-aesthetic" value={profile.aesthetic} onChange={(e) => setProfile({ ...profile, aesthetic: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light" />
        </div>
        <div className="bg-background p-5 sm:col-span-2">
          <p className="meta-mono mb-3">— BIO</p>
          <textarea data-testid="prof-bio" rows={4} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2" />
        </div>
        <div className="bg-background p-5 sm:col-span-2 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {profile.profile_image_url && <img src={profile.profile_image_url} alt="" className="w-16 h-16 object-cover border border-border" />}
            <label className="btn-pill cursor-pointer">
              <Upload size={14} /> {profile.profile_image_url ? "Replace portrait" : "Upload portrait"}
              <input data-testid="prof-avatar-upload" type="file" accept="image/*" hidden onChange={onUpload} />
            </label>
            {uploading && <span className="meta-mono !text-primary">Uploading…</span>}
          </div>
          <button data-testid="prof-save" onClick={save} disabled={saving} className="btn-pill btn-coral">{saving ? "Saving…" : <>Save profile <ArrowRight size={14} /></>}</button>
        </div>
      </div>
    </div>
  );
}

export function PortfolioSection() {
  const [profile, setProfile] = useState({ portfolio_urls: [] });
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    const { data } = await api.get("/profile/me");
    if (data) setProfile(data);
  };
  useEffect(() => { refresh(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const next = { ...profile, portfolio_urls: [...(profile.portfolio_urls || []), fullUrl] };
      await api.post("/profile", next);
      setProfile(next);
      toast.success("Added to portfolio");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (url) => {
    const next = { ...profile, portfolio_urls: profile.portfolio_urls.filter((u) => u !== url) };
    await api.post("/profile", next);
    setProfile(next);
    toast.success("Removed");
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <p className="meta-mono">— PORTFOLIO · {profile.portfolio_urls?.length || 0} PIECES</p>
        <label className="btn-pill btn-coral cursor-pointer">
          <Plus size={14} /> Add piece
          <input data-testid="portfolio-upload" type="file" accept="image/*,application/pdf" hidden onChange={upload} />
        </label>
      </div>
      {uploading && <p className="meta-mono !text-primary mb-4">Uploading…</p>}
      {(!profile.portfolio_urls || profile.portfolio_urls.length === 0) ? (
        <div className="border border-border p-12 text-center">
          <p className="font-display text-2xl font-bold mb-2">Your portfolio is <span className="hl-coral">empty.</span></p>
          <p className="text-white/65 text-sm">Add work to be discoverable by brands.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-border">
          {profile.portfolio_urls.map((u) => (
            <div key={u} className="bg-background relative group">
              <img src={u} alt="" className="w-full aspect-square object-cover" />
              <button onClick={() => remove(u)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-black/80 text-white border border-white/30 px-2 py-1 text-[10px] font-mono uppercase tracking-wider hover:bg-primary hover:border-primary">Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Inp({ label, v, on, testid }) {
  return (
    <div className="bg-background p-5">
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light" />
    </div>
  );
}
function Sel({ label, v, on, opts, testid }) {
  return (
    <div className="bg-background p-5">
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <select data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-lg font-light">
        {opts.map((o) => <option key={o} value={o} className="bg-background">{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
